import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    ChevronLeft,
    ChevronRight,
    EyeIcon,
    Loader2,
    Plus,
    RefreshCw,
    Search,
    Zap,
} from "lucide-react"
import moment from "moment"
import React, { useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"
import { useSearchParams } from "react-router-dom"
import Layout from "../../components/Layout"
import {
    Button,
    Card,
    CardHeader,
    CopyText,
    Input,
    Select,
    StatTile,
    Table,
    TBody,
    Td,
    Th,
    THead,
    Tr,
} from "../../components/ui"
import { cn } from "../../utils/cn"
import api from "../../utils/axios"
import DeviceModal, { DeviceModalState } from "./components/DeviceModal"
import { pairBadge } from "./components/pairBadge"
import { CodeWithPairs, Pair } from "./types"

// Re-export dos tipos do modelo de pares (compat com importadores existentes).
export type { CodeWithPairs, Pair, PairStatus, PixInfo } from "./types"

type FilterKey = "expired" | "expiring" | "active" | undefined

/** Uma linha da planilha: um par senha→link com o code achatado. */
interface DeviceRow extends Pair {
    code: string
}

type SortKey = "code" | "senha" | "createdat" | "validated" | "status"
type SortDir = "asc" | "desc"

const PAGE_SIZES = [25, 50, 100, 200]

// Célula compacta (planilha): mesmo padding em todas as linhas. A altura da
// linha é dada pelos controles de 32px (h-8) da coluna Ações, presentes ou não.
const cell = "py-2"

// Classifica um par nos baldes de faturamento (a unidade de receita é o par).
type PairBucket = "active" | "expiring" | "expired" | "other"
const pairBucket = (pair: Pair): PairBucket => {
    if (pair.status !== "ACTIVE") return "other"
    const d = pair.days_remaining
    if (d === null || d === undefined) return "active"
    if (d <= 0) return "expired"
    if (d <= 30) return "expiring"
    return "active"
}

// Ordem de status para ordenação: quem precisa de atenção primeiro.
const bucketRank: Record<PairBucket, number> = {
    expired: 0,
    expiring: 1,
    other: 2,
    active: 3,
}

const fmtDate = (v?: string | number | null, fallback = "sem data") =>
    v ? moment(v).format("DD/MM/YYYY HH:mm") : fallback

/** Debounce simples para a busca não refiltrar 100k linhas a cada tecla. */
function useDebounced<T>(value: T, ms: number) {
    const [debounced, setDebounced] = useState(value)
    useEffect(() => {
        const t = setTimeout(() => setDebounced(value), ms)
        return () => clearTimeout(t)
    }, [value, ms])
    return debounced
}

const DeviceListPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams()

    // ---- Filtros vivem na URL (fonte de verdade): ?filter=texto&status=active ----
    // Assim a lupa do modal, links compartilhados e o botão "voltar" funcionam,
    // e limpar tudo devolve /devices sem query string.
    const STATUS_KEYS: readonly FilterKey[] = ["active", "expiring", "expired"]
    const urlFilter = searchParams.get("filter") ?? ""
    const rawStatus = searchParams.get("status")
    const filterActive: FilterKey = STATUS_KEYS.includes(rawStatus as FilterKey)
        ? (rawStatus as FilterKey)
        : undefined

    /** Aplica mudanças de query param removendo chaves vazias. */
    const updateParams = (
        patch: Record<string, string | undefined>,
        opts: { replace?: boolean } = {}
    ) => {
        const next = new URLSearchParams(searchParams)
        for (const [k, v] of Object.entries(patch)) {
            if (v === undefined || v === "") next.delete(k)
            else next.set(k, v)
        }
        setSearchParams(next, { replace: opts.replace ?? false })
    }

    const setFilterActive = (key: FilterKey) => updateParams({ status: key })

    // O input é estado local (digitação fluida) e escreve na URL com debounce.
    const [searchTerm, setSearchTerm] = useState(urlFilter)
    const debouncedTerm = useDebounced(searchTerm.trim(), 200)
    useEffect(() => {
        if (debouncedTerm !== urlFilter)
            updateParams({ filter: debouncedTerm }, { replace: true })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedTerm])
    // URL mudou por fora (lupa do modal, voltar, link): sincroniza o input.
    useEffect(() => {
        setSearchTerm((cur) => (cur.trim() === urlFilter ? cur : urlFilter))
    }, [urlFilter])
    const term = urlFilter.toLowerCase()

    const [sortKey, setSortKey] = useState<SortKey>("createdat")
    const [sortDir, setSortDir] = useState<SortDir>("desc")

    const [pageSize, setPageSize] = useState(50)
    const [page, setPage] = useState(1)

    // Modal (inspecionar UM par / adicionar): estado local, não URL.
    const [modal, setModal] = useState<DeviceModalState | null>(null)

    // `/devices?add=1` (Dashboard e redirect de /generate) abre direto o modal
    // de adicionar; o parâmetro é consumido para não reabrir ao recarregar.
    useEffect(() => {
        if (searchParams.get("add")) {
            setModal({ mode: "create" })
            const next = new URLSearchParams(searchParams)
            next.delete("add")
            setSearchParams(next, { replace: true })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const {
        data: codes = [],
        isLoading: loading,
        isError,
        error,
        refetch: fetchDevices,
    } = useQuery<CodeWithPairs[], Error>(["fetchDeviceLinks"], async () => {
        const response = await api.post<CodeWithPairs[]>("/device-link/list")
        return response.data || []
    })

    // Achata code→pairs em UMA linha por par (planilha).
    const rows = useMemo<DeviceRow[]>(() => {
        const out: DeviceRow[] = []
        for (const c of codes) {
            for (const pair of c.pairs) out.push({ ...pair, code: c.code })
        }
        return out
    }, [codes])

    // StatTiles contam PARES (unidade de receita).
    const summary = useMemo(() => {
        const s = { total: rows.length, active: 0, expiring: 0, expired: 0 }
        for (const r of rows) {
            const b = pairBucket(r)
            if (b === "active") s.active++
            else if (b === "expiring") s.expiring++
            else if (b === "expired") s.expired++
        }
        return s
    }, [rows])

    const filtered = useMemo(() => {
        return rows.filter((r) => {
            if (filterActive !== undefined && pairBucket(r) !== filterActive)
                return false
            if (term === "") return true
            return (
                r.code.toLowerCase().includes(term) ||
                r.senha.toLowerCase().includes(term) ||
                (r.link ?? "").toLowerCase().includes(term)
            )
        })
    }, [rows, term, filterActive])

    const sorted = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1
        const cmp = (a: DeviceRow, b: DeviceRow): number => {
            switch (sortKey) {
                case "code":
                    return a.code.localeCompare(b.code)
                case "senha":
                    return a.senha.localeCompare(b.senha)
                case "createdat":
                    return (
                        (a.createdat ? Date.parse(a.createdat) : 0) -
                        (b.createdat ? Date.parse(b.createdat) : 0)
                    )
                case "validated":
                    return (a.validated ?? 0) - (b.validated ?? 0)
                case "status":
                    return (
                        bucketRank[pairBucket(a)] - bucketRank[pairBucket(b)] ||
                        (a.days_remaining ?? Infinity) -
                            (b.days_remaining ?? Infinity)
                    )
            }
        }
        return [...filtered].sort((a, b) => cmp(a, b) * dir || a.id - b.id)
    }, [filtered, sortKey, sortDir])

    // Reseta para a página 1 quando o conjunto muda.
    useEffect(() => {
        setPage(1)
    }, [term, filterActive, pageSize, sortKey, sortDir])

    const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize))
    const safePage = Math.min(page, totalPages)
    const pageRows = useMemo(
        () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
        [sorted, safePage, pageSize]
    )
    const firstIdx = sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1
    const lastIdx = Math.min(safePage * pageSize, sorted.length)

    function toggleSort(key: SortKey) {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"))
        } else {
            setSortKey(key)
            setSortDir(key === "createdat" || key === "validated" ? "desc" : "asc")
        }
    }

    const sortIndicator = (key: SortKey) =>
        sortKey !== key ? (
            <ArrowUpDown size={12} className="opacity-40" />
        ) : sortDir === "asc" ? (
            <ArrowUp size={12} />
        ) : (
            <ArrowDown size={12} />
        )

    const openInspect = (r: DeviceRow, autoActivate = false) =>
        setModal({ mode: "inspect", code: r.code, pairId: r.id, autoActivate })

    return (
        <Layout title="Dispositivos">
            <DeviceModal
                state={modal}
                onClose={() => setModal(null)}
                onApply={() => fetchDevices()}
            />

            {/* Faixa de resumo: conta PARES (unidade de receita) e filtra. */}
            <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatTile
                    label="Total"
                    value={summary.total}
                    accent="neutral"
                    active={filterActive === undefined}
                    onClick={() => setFilterActive(undefined)}
                />
                <StatTile
                    label="Ativos"
                    value={summary.active}
                    accent="success"
                    active={filterActive === "active"}
                    onClick={() => setFilterActive("active")}
                />
                <StatTile
                    label="Expirando"
                    value={summary.expiring}
                    accent="warning"
                    active={filterActive === "expiring"}
                    onClick={() => setFilterActive("expiring")}
                />
                <StatTile
                    label="Expirados"
                    value={summary.expired}
                    accent="danger"
                    active={filterActive === "expired"}
                    onClick={() => setFilterActive("expired")}
                />
            </div>

            <Card flush>
                <div className="border-b border-border p-5 sm:p-6">
                    <CardHeader
                        title="Acessos"
                        description="Uma linha por acesso (código · senha → link). Clique na linha para inspecionar."
                        actions={
                            <>
                                <Button
                                    variant="outline"
                                    onClick={() => fetchDevices()}
                                    disabled={loading}
                                    leftIcon={
                                        loading ? undefined : (
                                            <RefreshCw size={16} />
                                        )
                                    }
                                    loading={loading}
                                >
                                    Atualizar
                                </Button>
                                <Button
                                    type="button"
                                    leftIcon={<Plus size={16} />}
                                    onClick={() => setModal({ mode: "create" })}
                                >
                                    Adicionar
                                </Button>
                            </>
                        }
                    />

                    {/* Busca em linha própria, abaixo do título. O filtro por
                        status são os StatTiles no topo da página. */}
                    <div className="relative mt-4">
                        <Search
                            size={16}
                            aria-hidden="true"
                            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-subtle"
                        />
                        <Input
                            type="search"
                            placeholder="Buscar por código, senha ou link"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            aria-label="Buscar acessos"
                            className="pl-9"
                        />
                    </div>
                </div>

                {isError && error && (
                    <div className="m-4 rounded-lg border border-danger/40 bg-danger-soft p-4">
                        <p className="text-sm text-danger-text">
                            {error.message}
                        </p>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center px-4 py-16">
                        <Loader2 className="h-10 w-10 animate-spin text-primary" />
                    </div>
                ) : sorted.length === 0 ? (
                    <div className="px-6 py-16 text-center text-sm text-muted">
                        Nenhum acesso encontrado.
                    </div>
                ) : (
                    <>
                        <Table className="table-fixed min-w-[960px]">
                            <colgroup>
                                <col className="w-[120px]" />
                                <col className="w-[140px]" />
                                <col />
                                <col className="w-[150px]" />
                                <col className="w-[150px]" />
                                <col className="w-[150px]" />
                                <col className="w-[170px]" />
                            </colgroup>
                            <THead>
                                <tr>
                                    <Th
                                        sortable
                                        onClick={() => toggleSort("code")}
                                        indicator={sortIndicator("code")}
                                    >
                                        Código
                                    </Th>
                                    <Th
                                        sortable
                                        onClick={() => toggleSort("senha")}
                                        indicator={sortIndicator("senha")}
                                    >
                                        Senha
                                    </Th>
                                    <Th>Link da playlist</Th>
                                    <Th
                                        sortable
                                        onClick={() => toggleSort("createdat")}
                                        indicator={sortIndicator("createdat")}
                                    >
                                        Criado em
                                    </Th>
                                    <Th
                                        sortable
                                        onClick={() => toggleSort("validated")}
                                        indicator={sortIndicator("validated")}
                                    >
                                        Válido até
                                    </Th>
                                    <Th
                                        sortable
                                        onClick={() => toggleSort("status")}
                                        indicator={sortIndicator("status")}
                                    >
                                        Status
                                    </Th>
                                    <Th className="text-right">Ações</Th>
                                </tr>
                            </THead>
                            <TBody>
                                {pageRows.map((r) => (
                                    <Tr
                                        key={r.id}
                                        className="cursor-pointer"
                                        onClick={() => openInspect(r)}
                                    >
                                        <Td className={cell}>
                                            <CopyText value={r.code} />
                                        </Td>
                                        <Td className={cell}>
                                            <CopyText value={r.senha} />
                                        </Td>
                                        <Td className={cn(cell, "overflow-hidden")}>
                                            {r.link ? (
                                                <CopyText
                                                    value={r.link}
                                                    title={r.link}
                                                    hideIcon
                                                    className="flex w-full text-left text-xs font-normal tracking-normal text-muted hover:text-text"
                                                />
                                            ) : (
                                                <span className="text-xs uppercase text-subtle">
                                                    Não definido
                                                </span>
                                            )}
                                        </Td>
                                        <Td className={cn(cell, "whitespace-nowrap text-xs text-muted")}>
                                            {fmtDate(r.createdat)}
                                        </Td>
                                        <Td className={cn(cell, "whitespace-nowrap text-xs text-muted")}>
                                            {fmtDate(r.validated, "não ativado")}
                                        </Td>
                                        <Td className={cn(cell, "whitespace-nowrap")}>
                                            {pairBadge(
                                                r.status,
                                                r.days_remaining
                                            )}
                                        </Td>
                                        <Td className={cn(cell, "text-right")}>
                                            <div
                                                className="inline-flex h-8 items-center justify-end gap-1"
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            >
                                                {r.status !== "ACTIVE" && (
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="h-8 py-0"
                                                        leftIcon={
                                                            <Zap size={13} />
                                                        }
                                                        onClick={() =>
                                                            openInspect(r, true)
                                                        }
                                                    >
                                                        Ativar
                                                    </Button>
                                                )}
                                                <Button
                                                    size="icon"
                                                    variant="ghost"
                                                    className="h-8 w-8 p-0"
                                                    aria-label="Inspecionar acesso"
                                                    title="Inspecionar"
                                                    onClick={() =>
                                                        openInspect(r)
                                                    }
                                                >
                                                    <EyeIcon size={16} />
                                                </Button>
                                            </div>
                                        </Td>
                                    </Tr>
                                ))}
                            </TBody>
                        </Table>

                        {/* Paginação */}
                        <div className="flex flex-col gap-3 border-t border-border px-4 py-3 text-xs text-muted sm:flex-row sm:items-center sm:justify-between">
                            <span>
                                Mostrando {firstIdx}–{lastIdx} de{" "}
                                {sorted.length.toLocaleString("pt-BR")}
                                {sorted.length !== rows.length &&
                                    ` (de ${rows.length.toLocaleString(
                                        "pt-BR"
                                    )} no total)`}
                            </span>
                            <div className="flex items-center gap-2">
                                <Select
                                    value={pageSize}
                                    onChange={(e) =>
                                        setPageSize(Number(e.target.value))
                                    }
                                    className="w-auto py-1 text-xs"
                                    aria-label="Linhas por página"
                                >
                                    {PAGE_SIZES.map((n) => (
                                        <option key={n} value={n}>
                                            {n} / página
                                        </option>
                                    ))}
                                </Select>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    aria-label="Página anterior"
                                    disabled={safePage <= 1}
                                    onClick={() => setPage((p) => p - 1)}
                                >
                                    <ChevronLeft size={16} />
                                </Button>
                                <span
                                    className={cn(
                                        "min-w-[80px] text-center tabular-nums"
                                    )}
                                >
                                    {safePage} / {totalPages}
                                </span>
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    aria-label="Próxima página"
                                    disabled={safePage >= totalPages}
                                    onClick={() => setPage((p) => p + 1)}
                                >
                                    <ChevronRight size={16} />
                                </Button>
                            </div>
                        </div>
                    </>
                )}
            </Card>
        </Layout>
    )
}

export default DeviceListPage
